import React, { useEffect, useId, useState } from 'react'
import {
  BuildingIcon,
  CalendarIcon,
  ConfirmDeletionModal,
  EmptyState,
  type Contract,
  Mtds,
  SchemasScreenContent,
  TopBar,
  AppLayout,
  useGetPayloadSchemasQuery,
  useGetContractByIdQuery,
  ContractAnalysisRow,
  PlusIcon,
  XIcon,
  getCombinationStatus,
  useFilters,
  CombinationStatus,
  FlagIcon,
  getContractStatus,
  HourglassMediumIcon,
  PencilSimpleIcon,
  ScreenHeader,
  type ScreenHeaderProperty,
  toLocaleDateString,
  TrashIcon,
  useDeleteContractMutation,
  useErrorToast,
  useGetLabByIdQuery,
  UserIcon,
  useGetOwnerNameQuery
} from 'prove-common'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { AppLogo } from '../components/app-logo/AppLogo'
import { CombinationsTable } from '../tables/CombinationsTable'
import { CreateAnalysisTypeModal } from '../components/create-analysis-type-modal/CreateAnalysisTypeModal'
import { CombinationsFilterTags } from '../components/combinations-filter-tags/CombinationsFilterTags'
import type { PaginationState } from '@tanstack/react-table'
import { EditContractModal } from '../components/edit-contract-modal/EditContractModal'

type ContractScreenHeaderProps = {
  contract: Contract
}

const ContractScreenHeader = ({
  contract
}: Readonly<ContractScreenHeaderProps>) => {
  const navigate = useNavigate()
  const { data: owner } = useGetOwnerNameQuery(contract.ownerId)
  const { data: lab } = useGetLabByIdQuery(contract.labId)
  const deleteContract = useDeleteContractMutation()
  const showError = useErrorToast('slette kontrakten')
  const contractStatus = getContractStatus(contract)
  const [isOpenEditContractModal, setOpenEditContractModal] = useState(false)
  const [isOpenDeleteContractModal, setOpenDeleteContractModal] =
    useState(false)
  const contextMenuId = useId()
  const properties: ScreenHeaderProperty[] = [
    {
      label: 'Status',
      icon: <FlagIcon aria-hidden />,
      value: (
        <Mtds.Tag data-color={contractStatus.color}>
          {contractStatus.label}
        </Mtds.Tag>
      )
    },
    {
      label: 'Starter',
      icon: <CalendarIcon aria-hidden />,
      value: toLocaleDateString(contract.startDate) ?? '-'
    },
    {
      label: 'Slutter',
      icon: <HourglassMediumIcon aria-hidden />,
      value: toLocaleDateString(contract.endDate) ?? '-'
    },
    {
      label: 'Kontraktsansvarlig',
      icon: <UserIcon aria-hidden />,
      value: owner?.name ?? '-'
    },
    {
      label: 'Laboratorium',
      icon: <BuildingIcon aria-hidden />,
      value: lab?.name ?? '-'
    }
  ]

  return (
    <>
      <ScreenHeader
        title={contract.title}
        actions={
          <>
            <Mtds.Button
              aria-label='Handlingsmeny'
              popoverTarget={contextMenuId}
            />
            <Mtds.Popover as='menu' id={contextMenuId}>
              <li>
                <Mtds.Button onClick={() => setOpenEditContractModal(true)}>
                  <PencilSimpleIcon />
                  Rediger
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button
                  data-color='danger'
                  onClick={() => setOpenDeleteContractModal(true)}
                >
                  <TrashIcon />
                  Slett
                </Mtds.Button>
              </li>
            </Mtds.Popover>
          </>
        }
        properties={properties}
      />
      <EditContractModal
        isOpen={isOpenEditContractModal}
        setOpen={setOpenEditContractModal}
        contract={contract}
      />
      <ConfirmDeletionModal
        setOpen={() => setOpenDeleteContractModal(false)}
        isOpen={isOpenDeleteContractModal}
        onConfirm={() => {
          deleteContract
            .mutateAsync({ contractId: contract.id, etag: contract.etag! })
            .then(() => {
              Mtds.toast.success('Kontrakten er slettet')
              navigate('/contracts')
            })
            .catch((error) => {
              showError(error)
            })
        }}
        title={`Kontrakten «${contract.title}» vil bli permanent slettet.`}
        isPending={deleteContract.isPending}
      />
    </>
  )
}

export default function ContractScreen() {
  const { contractId } = useParams()
  const {
    data: contract,
    isPending: isPendingContract,
    isError: isErrorContract,
    isSuccess: isSuccessContract
  } = useGetContractByIdQuery(Number(contractId))
  const {
    data: payloadSchemas,
    isPending: isPendingPayloadSchemas,
    isError: isErrorPayloadSchemas,
    isSuccess: isSuccessPayloadSchemas
  } = useGetPayloadSchemasQuery(contract?.labId)
  const { filters, setFilters, isFiltering } =
    useFilters<CombinationStatus>('filterCombinations')
  const [searchParams, setSearchParams] = useSearchParams()
  const tabIndexParam = searchParams.get('tab')
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    tabIndexParam ? Number(tabIndexParam) : 0
  )
  const [showCreateAnalysisTypeCard, setShowCreateAnalysisTypeCard] =
    useState(false)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  })
  const isPending = isPendingContract || isPendingPayloadSchemas
  const isError = isErrorContract || isErrorPayloadSchemas
  const isSuccess = isSuccessContract && isSuccessPayloadSchemas
  const breadcrumbItems = [
    { label: 'Kontrakter', to: '/contracts' },
    { label: contract?.title }
  ]

  const getTableDataWithFilters = (): ContractAnalysisRow[] => {
    const contractEtag = contract?.etag
    if (!contract || !contractEtag) return []
    const filtered = (contract.analysisTypes ?? []).filter((analysisType) => {
      const st = analysisType?.analysisType?.sampleType
      const productId = st?.productId.toString()
      const matrixId = st?.matrixId.toString()
      return (
        (filters.productIds?.length === 0 ||
          filters.productIds?.includes(productId ?? '')) &&
        (filters.matrixIds?.length === 0 ||
          filters.matrixIds?.includes(matrixId ?? ''))
      )
    })

    return filtered.map((analysisType) => ({
      contractId: contract.id,
      contractEtag,
      analysisType,
      status: getCombinationStatus(analysisType)
    }))
  }
  const tableData = getTableDataWithFilters()
  const selectedStatus =
    filters.status && filters.status.length > 0
      ? filters.status[0]?.toString()
      : 'all'
  const selectedTableData =
    selectedStatus === 'all'
      ? tableData
      : tableData.filter((row) => row.status === selectedStatus)

  const counts = {
    awaiting: tableData.filter((d) => d.status === 'awaiting').length,
    approved: tableData.filter((d) => d.status === 'approved').length,
    declined: tableData.filter((d) => d.status === 'declined').length,
    all: tableData.length
  }

  const items = [
    ['awaiting', `Avventer godkjenning (${counts.awaiting})`],
    ['approved', `Godkjent (${counts.approved})`],
    ['declined', `Avslått (${counts.declined})`],
    ['all', `Alle (${counts.all})`]
  ] as const

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', selectedTabIndex.toString())
    setSearchParams(next, { replace: true })
  }, [selectedTabIndex, searchParams])
  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isPending}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={breadcrumbItems}
          showNotificationBell={true}
        />
      }
      isFullWidth
    >
      {isSuccess && (
        <>
          <ContractScreenHeader contract={contract} />
          <Mtds.Tabs>
            <Mtds.Tabs.List>
              <Mtds.Tabs.Tab
                aria-selected={selectedTabIndex === 0}
                onClick={() => setSelectedTabIndex(0)}
              >
                Tilgjengelige analyser
              </Mtds.Tabs.Tab>
              <Mtds.Tabs.Tab
                aria-selected={selectedTabIndex === 1}
                onClick={() => setSelectedTabIndex(1)}
              >
                Registreringsskjemaer
              </Mtds.Tabs.Tab>
            </Mtds.Tabs.List>
            <Mtds.Tabs.Panel hidden={selectedTabIndex !== 0}>
              <Mtds.Grid>
                <Mtds.Flex data-justify='space-between' data-align={'center'}>
                  <Mtds.Heading>Tilgjengelige analyser</Mtds.Heading>
                  <Mtds.Button
                    data-variant={'primary'}
                    onClick={() => setShowCreateAnalysisTypeCard(true)}
                  >
                    <PlusIcon />
                    Legg til analysetype
                  </Mtds.Button>
                </Mtds.Flex>
                <Mtds.Flex>
                  <Mtds.Togglegroup
                    {...({ focusgroup: 'radiogroup' } as Record<
                      string,
                      string
                    >)}
                    aria-label='Status for analyser'
                  >
                    {items.map(([value, label]) => (
                      <Mtds.Togglegroup.Item
                        name='combinations-toggle'
                        key={value}
                        value={value}
                        checked={selectedStatus === value}
                        onChange={() => {
                          setFilters({
                            ...filters,
                            status: value === 'all' ? [] : [value]
                          })
                          setPagination({
                            pageIndex: 0,
                            pageSize: pagination.pageSize
                          })
                        }}
                      >
                        {label}
                      </Mtds.Togglegroup.Item>
                    ))}
                  </Mtds.Togglegroup>
                  <CombinationsFilterTags
                    filters={filters}
                    setFilters={(updatedFilters) => {
                      setFilters(updatedFilters)
                      setPagination({
                        pageIndex: 0,
                        pageSize: pagination.pageSize
                      })
                    }}
                  />
                  {isFiltering && (
                    <Mtds.Button
                      data-cy='clear-filters'
                      onClick={() => {
                        setFilters({
                          productIds: [],
                          sampntTypeIds: [],
                          substanceIds: [],
                          planIds: [],
                          matrixIds: [],
                          status: []
                        })
                        setPagination({
                          pageIndex: 0,
                          pageSize: pagination.pageSize
                        })
                      }}
                    >
                      Fjern filtre
                      <XIcon />
                    </Mtds.Button>
                  )}
                </Mtds.Flex>
                <CreateAnalysisTypeModal
                  contract={contract}
                  isOpen={showCreateAnalysisTypeCard}
                  setOpen={() => setShowCreateAnalysisTypeCard(false)}
                />

                {selectedTableData.length ? (
                  <CombinationsTable
                    data={selectedTableData}
                    pagination={pagination}
                    setPagination={setPagination}
                  />
                ) : (
                  <EmptyState
                    title={
                      isFiltering
                        ? 'Ingen analysetyper samsvarer med filtrene'
                        : 'Ingen analysetyper'
                    }
                    description={
                      isFiltering
                        ? 'Prøv andre filtervalg for å se flere analysetyper.'
                        : 'Legg til en analysetype for å komme i gang.'
                    }
                  />
                )}
              </Mtds.Grid>
            </Mtds.Tabs.Panel>
            <Mtds.Tabs.Panel hidden={selectedTabIndex !== 1}>
              <SchemasScreenContent
                payloadSchemas={payloadSchemas ?? []}
                contractId={Number(contractId)}
                labId={contract?.labId}
              />
            </Mtds.Tabs.Panel>
          </Mtds.Tabs>
        </>
      )}
    </AppLayout>
  )
}
